<div class="stacksets-update-instructions">
  <!-- Setup Mode: StackSets not yet configured -->
  @if (isSetupMode) {
    <h2>Set up StackSets</h2>
    <p class="intro">
      You've chosen to manage {{ uiService.productName }} access with CloudFormation StackSets. Follow these steps to
      deploy StackSets and grant {{ uiService.productName }} access to your AWS accounts.
    </p>
  }

  <!-- Update Mode: StackSets already configured -->
  @if (!isSetupMode) {
    <h2>Update StackSets</h2>
    <p class="intro">
      We've enhanced {{ uiService.productName }}, but to get the latest functionality for this application, you'll need
      to update your StackSets.
    </p>
  }

  <!-- Gated wrapper — heading + intro above stay clear; the config box, info notice -->
  <!-- (now positioned after the config so users see "what to run" right above the commands), -->
  <!-- and command groups inside this wrapper get the dim/blur and spinner overlay. -->
  <div class="stacksets-gated-content" [class.regen-pending]="regenerationInProgress">
    <div class="stackset-names-section">
      <p class="names-intro">
        <i class="fa-solid fa-circle-info"></i>
        <span
          >Enter your management account topology, account ID(s), and StackSet names to generate the
          {{ isSetupMode ? 'create' : 'update' }} commands. We'll remember these in this browser for next time.</span
        >
      </p>

      <label class="topology-toggle">
        <input type="checkbox" [checked]="!isTwoMgmtAccounts" (change)="onSingleMgmtToggle($event)" />
        <span class="topology-toggle-label">
          <strong>Use a single management account for both Primary and Recovery</strong>
          <span class="topology-toggle-hint"
            >Leave unchecked if Primary and Recovery are deployed from separate management accounts.</span
          >
        </span>
      </label>

      <div class="topology-inputs" [class.is-single]="!isTwoMgmtAccounts" [class.is-separate]="isTwoMgmtAccounts">
        <!-- Row 1: management account ID(s) -->
        @if (!isTwoMgmtAccounts) {
          <div class="topology-cell mgmt full-width">
            <label for="update-management-account-id">
              <i class="fa-solid fa-shield-halved"></i>
              Management Account ID
            </label>
            <input
              type="text"
              id="update-management-account-id"
              [(ngModel)]="primaryManagementAccountId"
              placeholder="e.g., 123456789012"
            />
          </div>
        }
        @if (isTwoMgmtAccounts) {
          <div class="topology-cell mgmt primary-col">
            <label for="update-primary-management-account-id">
              <i class="fa-solid fa-shield-halved"></i>
              Primary Management Account ID
            </label>
            <input
              type="text"
              id="update-primary-management-account-id"
              [(ngModel)]="primaryManagementAccountId"
              placeholder="e.g., 123456789012"
            />
          </div>
          <div class="topology-cell mgmt recovery-col">
            <label for="update-recovery-management-account-id">
              <i class="fa-solid fa-shield-halved"></i>
              Recovery Management Account ID
            </label>
            <input
              type="text"
              id="update-recovery-management-account-id"
              [(ngModel)]="recoveryManagementAccountId"
              placeholder="e.g., 123456789012"
            />
          </div>
        }

        <!-- Row 2: stackset names -->
        <div class="topology-cell stackset primary-col">
          <label for="update-primary-stackset-name">
            <i class="fa-solid fa-location-dot"></i>
            Primary Access StackSet Name
          </label>
          <input
            type="text"
            id="update-primary-stackset-name"
            [(ngModel)]="primaryStackSetName"
            placeholder="e.g., ArpioPrimaryAccess"
          />
        </div>
        <div class="topology-cell stackset recovery-col">
          <label for="update-recovery-stackset-name">
            <i class="fa-solid fa-location-crosshairs"></i>
            Recovery Access StackSet Name
          </label>
          <input
            type="text"
            id="update-recovery-stackset-name"
            [(ngModel)]="recoveryStackSetName"
            placeholder="e.g., ArpioRecoveryAccess"
          />
        </div>
      </div>
    </div>

    <!-- Info Notice for Setup -->
    @if (isSetupMode) {
      <div class="info-notice">
        <i class="fa-solid fa-circle-info"></i>
        <div class="notice-content">
          @if (!isTwoMgmtAccounts) {
            <p>Run these commands from your <strong>management account</strong> to create the StackSets:</p>
          }
          @if (isTwoMgmtAccounts) {
            <p>
              Run each command from its matching management account: the Primary StackSet from your
              <strong>Primary management account</strong> (<code>{{ primaryMgmtAccountPlaceholder }}</code
              >) and the Recovery StackSet from your <strong>Recovery management account</strong> (<code>{{
                recoveryMgmtAccountPlaceholder
              }}</code
              >):
            </p>
          }
          <div class="affected-accounts">
            <strong>Member accounts:</strong>
            <span class="account-tags">
              @for (accountId of uniqueAccountIds; track accountId) {
                <code>{{ accountId }}</code>
              }
            </span>
          </div>
        </div>
      </div>
    }

    <!-- Info Notice for Update -->
    @if (!isSetupMode) {
      <div class="info-notice">
        <i class="fa-solid fa-circle-info"></i>
        <div class="notice-content">
          @if (!isTwoMgmtAccounts) {
            <p>
              Run the following commands from your <strong>management account</strong> to update the StackSets. This
              will update all stack instances in your StackSets, applying the latest permissions for all
              {{ uiService.productName }} applications at once:
            </p>
          }
          @if (isTwoMgmtAccounts) {
            <p>
              Run each command from its matching management account &mdash; the Primary update from your
              <strong>Primary management account</strong> (<code>{{ primaryMgmtAccountPlaceholder }}</code
              >) and the Recovery update from your <strong>Recovery management account</strong> (<code>{{
                recoveryMgmtAccountPlaceholder
              }}</code
              >). This will update all stack instances in your StackSets, applying the latest permissions for all
              {{ uiService.productName }} applications at once:
            </p>
          }
          <div class="affected-accounts">
            <strong>Affected accounts:</strong>
            <span class="account-tags">
              @for (accountId of uniqueAccountIds; track accountId) {
                <code>{{ accountId }}</code>
              }
            </span>
          </div>
        </div>
      </div>
    }

    <div class="command-group">
      <h4>
        <i class="fa-solid fa-terminal"></i>
        {{ isSetupMode ? 'Create' : 'Update' }} Primary Access StackSet
      </h4>
      @if (isTwoMgmtAccounts) {
        <div class="location-notice">
          <i class="fa-solid fa-circle-info"></i>
          <span
            >Run this from your <strong>Primary management account</strong> (<code>{{
              primaryMgmtAccountPlaceholder
            }}</code
            >).</span
          >
        </div>
      }
      <!-- Test Access for Primary -->
      @if (sourceIsLatest) {
        <div class="configured-notice">
          <i class="fa-solid fa-circle-check"></i>
          <span>Primary access is up to date</span>
        </div>
      } @else {
        <!-- Access already works on the deployed template version. The notice-block
        below stays put so TEST ACCESS remains reachable after an update. -->
        @if (showSourceConnectedNotice) {
          <div class="stale-notice" data-cy="stacksets-source-stale-notice">
            <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
            <span>Primary access is out of date</span>
          </div>
        }
        <div class="notice-block">
          <div class="notice-text">
            @if (sourceRetryText && !isTestingSource) {
              <strong class="retry-text">{{ sourceRetryText }}</strong>
            }
            <span>After running the update command, allow 1–3 minutes for changes to propagate, then verify.</span>
          </div>
          <button
            class="arpio-button small-button test-access-btn"
            [disabled]="isTestingSource"
            (click)="onTestAccess('source')"
          >
            @if (isTestingSource) {
              <i class="fa-solid fa-spinner fa-spin"></i> TESTING...
            } @else {
              TEST ACCESS
            }
          </button>
        </div>
      }
      <div class="command-block">
        <div class="command-actions">
          <button class="copy-btn" [copy-clipboard]="primaryStackSetCommand" (copied)="onCopied('primary')">
            <i
              class="fa-utility fa-semibold"
              [class.fa-copy]="!isCopied('primary')"
              [class.fa-check]="isCopied('primary')"
            ></i>
            {{ isCopied('primary') ? 'Copied!' : 'Copy' }}
          </button>
          <button class="download-btn" (click)="downloadCommand('primary')" title="Download as shell script">
            <i class="fa-solid fa-download"></i>
            Download
          </button>
        </div>
        <pre><code>{{ primaryStackSetCommand }}</code></pre>
      </div>
    </div>

    <div class="command-group">
      <h4>
        <i class="fa-solid fa-terminal"></i>
        {{ isSetupMode ? 'Create' : 'Update' }} Recovery Access StackSet
      </h4>
      @if (isTwoMgmtAccounts) {
        <div class="location-notice">
          <i class="fa-solid fa-circle-info"></i>
          <span
            >Run this from your <strong>Recovery management account</strong> (<code>{{
              recoveryMgmtAccountPlaceholder
            }}</code
            >).</span
          >
        </div>
      }
      <!-- Test Access for Recovery -->
      @if (targetIsLatest) {
        <div class="configured-notice">
          <i class="fa-solid fa-circle-check"></i>
          <span>Recovery access is up to date</span>
        </div>
      } @else {
        @if (showTargetConnectedNotice) {
          <div class="stale-notice" data-cy="stacksets-target-stale-notice">
            <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
            <span>Recovery access is out of date</span>
          </div>
        }
        <div class="notice-block">
          <div class="notice-text">
            @if (targetRetryText && !isTestingTarget) {
              <strong class="retry-text">{{ targetRetryText }}</strong>
            }
            <span>After running the update command, allow 1–3 minutes for changes to propagate, then verify.</span>
          </div>
          <button
            class="arpio-button small-button test-access-btn"
            [disabled]="isTestingTarget"
            (click)="onTestAccess('target')"
          >
            @if (isTestingTarget) {
              <i class="fa-solid fa-spinner fa-spin"></i> TESTING...
            } @else {
              TEST ACCESS
            }
          </button>
        </div>
      }
      <div class="command-block">
        <div class="command-actions">
          <button class="copy-btn" [copy-clipboard]="recoveryStackSetCommand" (copied)="onCopied('recovery')">
            <i
              class="fa-utility fa-semibold"
              [class.fa-copy]="!isCopied('recovery')"
              [class.fa-check]="isCopied('recovery')"
            ></i>
            {{ isCopied('recovery') ? 'Copied!' : 'Copy' }}
          </button>
          <button class="download-btn" (click)="downloadCommand('recovery')" title="Download as shell script">
            <i class="fa-solid fa-download"></i>
            Download
          </button>
        </div>
        <pre><code>{{ recoveryStackSetCommand }}</code></pre>
      </div>
    </div>

    <!-- Spinner overlay — blocks the update command UI until backend regen of all sync pairs is done. -->
    <!-- Unlike the 3-step setup flow, the update command rolls out templates to ALL stack instances at -->
    <!-- once, so it can't be safely run until every sync pair's template has been regenerated. -->
    @if (regenerationInProgress) {
      <div class="regen-spinner-overlay">
        <div class="regen-spinner-pill">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <p>Preparing templates&hellip;</p>
        </div>
      </div>
    }
  </div>
</div>
